// Rastreadores GPS da frota: cadastro do aparelho (com os limites de bateria),
// vínculo com o veículo e o que cada aparelho envia (eventos, bateria e
// mensagens brutas). Um aparelho novo aparece em "Aparelhos não cadastrados"
// assim que conecta — é de onde sai o ID. ?battery=<id> abre a aba Bateria
// (link do sininho).

import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Edit, MapPin, Plus, Radio, RefreshCw, Trash2 } from 'lucide-react';
import { Button } from '@components/Button';
import { Badge, Card, CardContent } from '@components/Card';
import { Dialog } from '@components/Dialog';
import { Input, Select, Textarea } from '@components/Input';
import { PermissionGuard } from '@components/PermissionGuard';
import { SimpleTable as Table, TableCell, TableHeader, TableRow } from '@components/Table';
import { formatDateTimeBR } from '@pages/financial/financialUtils';
import { vehicleService } from '@services/vehicleService';
import { vehicleTrackerService } from '@services/vehicleTrackerService';
import type { Vehicle } from '@/types/vehicle';
import type {
  TrackerEvent,
  TrackerRawMessage,
  UnknownTrackerDevice,
  VehicleTracker,
  VehicleTrackerFormData,
} from '@/types/vehicleTracker';
import { TrackerBatteryPanel } from './TrackerBatteryPanel';
import {
  batteryVariant,
  commStatus,
  DEFAULT_BATTERY_CRITICAL,
  DEFAULT_BATTERY_DEAD_SILENCE_MIN,
  DEFAULT_BATTERY_LOW,
  eventLabel,
  eventVariant,
  formatAgo,
  mapsLink,
  rawTypeLabel,
} from './trackerUtils';

const emptyForm: VehicleTrackerFormData = { device_id: '', is_active: true };

const errorMessage = (err: unknown, fallback: string) => (err instanceof Error ? err.message : fallback);

// ─── Cadastro ───────────────────────────────────────────────────────────────

interface FormDialogProps {
  isOpen: boolean;
  editing: VehicleTracker | null;
  initialDeviceId: string;
  vehicles: Vehicle[];
  onClose: () => void;
  onSaved: () => void;
}

const TrackerFormDialog: React.FC<FormDialogProps> = ({ isOpen, editing, initialDeviceId, vehicles, onClose, onSaved }) => {
  const [form, setForm] = useState<VehicleTrackerFormData>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setForm(
      editing
        ? {
            device_id: editing.device_id,
            imei: editing.imei,
            model: editing.model,
            sim_number: editing.sim_number,
            vehicle_id: editing.vehicle_id,
            is_active: editing.is_active,
            notes: editing.notes,
            battery_low_percent: editing.battery_low_percent,
            battery_critical_percent: editing.battery_critical_percent,
            battery_dead_silence_minutes: editing.battery_dead_silence_minutes,
          }
        : { ...emptyForm, device_id: initialDeviceId, model: 'TKSTAR TK915-4G' },
    );
  }, [isOpen, editing, initialDeviceId]);

  const set = <K extends keyof VehicleTrackerFormData>(key: K, value: VehicleTrackerFormData[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));
  // campo numérico vazio = padrão do serviço
  const setNumber = (key: 'battery_low_percent' | 'battery_critical_percent' | 'battery_dead_silence_minutes', raw: string) =>
    set(key, raw.trim() === '' ? undefined : Math.trunc(Number(raw)));

  const handleSave = async () => {
    if (!form.device_id.trim()) {
      setError('Informe o ID do rastreador');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      if (editing) await vehicleTrackerService.update(editing.vehicle_tracker_id, form);
      else await vehicleTrackerService.create(form);
      onSaved();
    } catch (err) {
      setError(errorMessage(err, 'Erro ao salvar'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={editing ? 'Editar rastreador' : 'Novo rastreador'}
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">{error}</div>}
        <Input
          label="ID do rastreador"
          required
          value={form.device_id}
          onChange={(e) => set('device_id', e.target.value)}
          helperText="É o ID de 10 dígitos que o aparelho informa na resposta do SMS check123456 — não é o IMEI."
          maxLength={20}
        />
        <Select
          label="Veículo"
          value={form.vehicle_id ?? ''}
          onChange={(e) => set('vehicle_id', e.target.value ? Number(e.target.value) : undefined)}
        >
          <option value="">Sem veículo</option>
          {vehicles.map((v) => (
            <option key={v.vehicle_id} value={v.vehicle_id}>
              {v.license_plate} — {v.description}
            </option>
          ))}
        </Select>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input label="IMEI" value={form.imei ?? ''} onChange={(e) => set('imei', e.target.value)} maxLength={20} />
          <Input
            label="Número do chip"
            value={form.sim_number ?? ''}
            onChange={(e) => set('sim_number', e.target.value)}
            maxLength={30}
          />
        </div>
        <Input label="Modelo" value={form.model ?? ''} onChange={(e) => set('model', e.target.value)} maxLength={60} />
        <Textarea label="Observações" rows={2} value={form.notes ?? ''} onChange={(e) => set('notes', e.target.value)} maxLength={500} />
        <div>
          <p className="text-sm font-medium text-slate-700 mb-1">Alertas de bateria</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Input
              label="Aviso (%)"
              type="number"
              min={5}
              max={95}
              placeholder={String(DEFAULT_BATTERY_LOW)}
              value={form.battery_low_percent ?? ''}
              onChange={(e) => setNumber('battery_low_percent', e.target.value)}
            />
            <Input
              label="Crítico (%)"
              type="number"
              min={1}
              max={90}
              placeholder={String(DEFAULT_BATTERY_CRITICAL)}
              value={form.battery_critical_percent ?? ''}
              onChange={(e) => setNumber('battery_critical_percent', e.target.value)}
            />
            <Input
              label="Desligado após (min)"
              type="number"
              min={10}
              max={1440}
              placeholder={String(DEFAULT_BATTERY_DEAD_SILENCE_MIN)}
              value={form.battery_dead_silence_minutes ?? ''}
              onChange={(e) => setNumber('battery_dead_silence_minutes', e.target.value)}
            />
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Vazio usa o padrão ({DEFAULT_BATTERY_LOW}% / {DEFAULT_BATTERY_CRITICAL}% / {DEFAULT_BATTERY_DEAD_SILENCE_MIN} min). Sem
            comunicação além do tempo, com a bateria até o aviso, o aparelho conta como desligado por bateria (o TK913 só volta no
            botão).
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={form.is_active} onChange={(e) => set('is_active', e.target.checked)} />
          Ativo (as posições só são gravadas para rastreador ativo)
        </label>
      </div>
    </Dialog>
  );
};

// ─── Detalhe: eventos e mensagens brutas ────────────────────────────────────

type DetailTab = 'events' | 'raw' | 'battery';

const TrackerDetailDialog: React.FC<{ tracker: VehicleTracker | null; initialTab: DetailTab; onClose: () => void }> = ({
  tracker,
  initialTab,
  onClose,
}) => {
  const [tab, setTab] = useState<DetailTab>(initialTab);
  const [events, setEvents] = useState<TrackerEvent[]>([]);
  const [raw, setRaw] = useState<TrackerRawMessage[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const trackerId = tracker?.vehicle_tracker_id;
  const pageSize = 100;

  const load = useCallback(async () => {
    if (!trackerId) return;
    try {
      setLoading(true);
      setError(null);
      const [ev, messages] = await Promise.all([
        vehicleTrackerService.listEvents(trackerId),
        vehicleTrackerService.listRawMessages(trackerId, pageSize),
      ]);
      setEvents(ev);
      setRaw(messages);
      setHasMore(messages.length === pageSize);
    } catch (err) {
      setError(errorMessage(err, 'Erro ao carregar'));
    } finally {
      setLoading(false);
    }
  }, [trackerId]);

  useEffect(() => {
    setEvents([]);
    setRaw([]);
    load();
  }, [load]);

  useEffect(() => {
    if (tracker) setTab(initialTab);
  }, [tracker, initialTab]);

  const loadMore = async () => {
    if (!trackerId || raw.length === 0) return;
    try {
      setLoading(true);
      const older = await vehicleTrackerService.listRawMessages(trackerId, pageSize, raw[raw.length - 1].tracker_raw_message_id);
      setRaw((prev) => [...prev, ...older]);
      setHasMore(older.length === pageSize);
    } catch (err) {
      setError(errorMessage(err, 'Erro ao carregar'));
    } finally {
      setLoading(false);
    }
  };

  const tabCls = (active: boolean) =>
    `px-3 py-2 text-sm font-medium border-b-2 ${active ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`;

  return (
    <Dialog
      isOpen={!!tracker}
      onClose={onClose}
      title={tracker ? `Rastreador ${tracker.device_id}${tracker.vehicle_label ? ` — ${tracker.vehicle_label}` : ''}` : ''}
      size="xl"
      footer={
        <div className="flex justify-end">
          <Button variant="secondary" onClick={onClose}>
            Fechar
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <div className="flex items-center justify-between border-b border-slate-200">
          <div className="flex gap-1">
            <button type="button" className={tabCls(tab === 'raw')} onClick={() => setTab('raw')}>
              Mensagens brutas ({raw.length}
              {hasMore ? '+' : ''})
            </button>
            <button type="button" className={tabCls(tab === 'events')} onClick={() => setTab('events')}>
              Eventos ({events.length})
            </button>
            <button type="button" className={tabCls(tab === 'battery')} onClick={() => setTab('battery')}>
              Bateria
            </button>
          </div>
          {tab !== 'battery' && (
            <Button variant="secondary" size="sm" onClick={load} disabled={loading} icon={<RefreshCw size={14} />}>
              Atualizar
            </Button>
          )}
        </div>

        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">{error}</div>}

        {tab === 'raw' && (
          <div className="space-y-2">
            <p className="text-xs text-slate-500">
              Tudo o que o aparelho enviou, como chegou. Mensagens começando com <span className="font-mono">hex:</span> não são do
              protocolo esperado.
            </p>
            <div className="max-h-[55vh] overflow-auto border border-slate-200 rounded-lg">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0">
                  <tr className="text-left text-slate-600">
                    <th className="px-2 py-2 whitespace-nowrap">Recebido em</th>
                    <th className="px-2 py-2">Tipo</th>
                    <th className="px-2 py-2">Conteúdo</th>
                  </tr>
                </thead>
                <tbody>
                  {raw.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="px-2 py-6 text-center text-slate-500">
                        {loading ? 'Carregando...' : 'Nenhuma mensagem recebida deste aparelho'}
                      </td>
                    </tr>
                  ) : (
                    raw.map((m) => (
                      <tr key={m.tracker_raw_message_id} className="border-t border-slate-100 align-top">
                        <td className="px-2 py-1.5 whitespace-nowrap">{formatDateTimeBR(m.received_at)}</td>
                        <td className="px-2 py-1.5 whitespace-nowrap">
                          <Badge variant={m.parse_ok ? 'neutral' : 'danger'} size="sm">
                            {rawTypeLabel(m.message_type)}
                          </Badge>
                        </td>
                        <td className="px-2 py-1.5">
                          <div className="font-mono break-all">{m.payload}</div>
                          {m.parse_error && <div className="text-amber-700 mt-0.5">{m.parse_error}</div>}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {hasMore && (
              <div className="flex justify-center">
                <Button variant="secondary" size="sm" onClick={loadMore} disabled={loading}>
                  Carregar mais antigas
                </Button>
              </div>
            )}
          </div>
        )}

        {tab === 'battery' && trackerId && <TrackerBatteryPanel trackerId={trackerId} />}

        {tab === 'events' && (
          <div className="max-h-[60vh] overflow-auto border border-slate-200 rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0">
                <tr className="text-left text-slate-600">
                  <th className="px-3 py-2 whitespace-nowrap">Quando</th>
                  <th className="px-3 py-2">Evento</th>
                  <th className="px-3 py-2">Detalhe</th>
                  <th className="px-3 py-2">Local</th>
                </tr>
              </thead>
              <tbody>
                {events.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-3 py-6 text-center text-slate-500">
                      {loading ? 'Carregando...' : 'Nenhum evento registrado'}
                    </td>
                  </tr>
                ) : (
                  events.map((e) => (
                    <tr key={e.tracker_event_id} className="border-t border-slate-100">
                      <td className="px-3 py-1.5 whitespace-nowrap">{formatDateTimeBR(e.event_at)}</td>
                      <td className="px-3 py-1.5">
                        <Badge variant={eventVariant(e.event_type)} size="sm">
                          {eventLabel(e.event_type)}
                        </Badge>
                      </td>
                      <td className="px-3 py-1.5 text-slate-600">{e.detail || '—'}</td>
                      <td className="px-3 py-1.5">
                        {e.latitude != null && e.longitude != null ? (
                          <a
                            className="text-emerald-700 hover:underline inline-flex items-center gap-1"
                            href={mapsLink(e.latitude, e.longitude)}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <MapPin size={14} /> ver no mapa
                          </a>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Dialog>
  );
};

// ─── Tela ───────────────────────────────────────────────────────────────────

const TrackersContent: React.FC = () => {
  const [items, setItems] = useState<VehicleTracker[]>([]);
  const [unknown, setUnknown] = useState<UnknownTrackerDevice[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<VehicleTracker | null>(null);
  const [initialDeviceId, setInitialDeviceId] = useState('');
  const [detail, setDetail] = useState<VehicleTracker | null>(null);
  const [detailTab, setDetailTab] = useState<DetailTab>('raw');
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [trackers, unknownDevices] = await Promise.all([vehicleTrackerService.list(), vehicleTrackerService.listUnknown()]);
      setItems(trackers);
      setUnknown(unknownDevices);
    } catch (err) {
      setError(errorMessage(err, 'Erro ao carregar dados'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    vehicleService
      .getAll({ page: 1, pageSize: 200 })
      .then((result) => setVehicles(result.data))
      .catch(() => setVehicles([]));
  }, [load]);

  // ?battery=<id> (link do sininho): abre a aba Bateria do rastreador
  const batteryParam = searchParams.get('battery');
  useEffect(() => {
    if (!batteryParam || items.length === 0) return;
    const target = items.find((t) => t.vehicle_tracker_id === Number(batteryParam));
    if (target) {
      setDetailTab('battery');
      setDetail(target);
    }
    setSearchParams({}, { replace: true });
  }, [batteryParam, items, setSearchParams]);

  const openDetail = (tracker: VehicleTracker, tab: DetailTab) => {
    setDetailTab(tab);
    setDetail(tracker);
  };

  const openForm = (tracker: VehicleTracker | null, deviceId = '') => {
    setEditing(tracker);
    setInitialDeviceId(deviceId);
    setFormOpen(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await vehicleTrackerService.delete(id);
      setDeleteConfirm(null);
      load();
    } catch (err) {
      setDeleteConfirm(null);
      setError(errorMessage(err, 'Erro ao excluir'));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-h1">Rastreadores</h1>
          <p className="text-slate-600">Aparelhos GPS da frota, o veículo de cada um e o que eles estão enviando</p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={load} disabled={loading} icon={<RefreshCw size={18} />}>
            Atualizar
          </Button>
          <Button onClick={() => openForm(null)} icon={<Plus size={20} />}>
            Novo Rastreador
          </Button>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>}

      {unknown.length > 0 && (
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-amber-800">
              <Radio size={18} />
              <h2 className="font-semibold">Aparelhos não cadastrados</h2>
            </div>
            <p className="text-sm text-slate-600">
              Estes aparelhos conectaram nos últimos 7 dias e ainda não estão cadastrados. Enquanto não forem cadastrados, só as
              mensagens brutas são guardadas — nenhuma posição.
            </p>
            <div className="overflow-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-600">
                    <th className="py-1 pr-3">ID</th>
                    <th className="py-1 pr-3">Mensagens</th>
                    <th className="py-1 pr-3">Última</th>
                    <th className="py-1 pr-3">Origem</th>
                    <th className="py-1 pr-3">Último conteúdo</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {unknown.map((d) => (
                    <tr key={d.device_id} className="border-t border-slate-100 align-top">
                      <td className="py-1.5 pr-3 font-mono">{d.device_id}</td>
                      <td className="py-1.5 pr-3">{d.messages}</td>
                      <td className="py-1.5 pr-3 whitespace-nowrap">{formatDateTimeBR(d.last_seen_at)}</td>
                      <td className="py-1.5 pr-3 font-mono text-xs">{d.last_remote_addr}</td>
                      <td className="py-1.5 pr-3 font-mono text-xs break-all">{d.last_payload}</td>
                      <td className="py-1.5 text-right">
                        <Button size="sm" onClick={() => openForm(null, d.device_id)}>
                          Cadastrar
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <tr>
                <th className="text-left">ID</th>
                <th className="text-left">Veículo</th>
                <th className="text-left">Comunicação</th>
                <th className="text-left">Última posição</th>
                <th className="text-center">Bateria</th>
                <th className="text-center">Sinal</th>
                <th className="text-center">Status</th>
                <th className="text-center">Ações</th>
              </tr>
            </TableHeader>
            <tbody>
              {loading && items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                    Carregando...
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                    Nenhum rastreador cadastrado
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item) => {
                  const comm = commStatus(item.last_seen_seconds_ago);
                  return (
                    <TableRow key={item.vehicle_tracker_id}>
                      <TableCell>
                        <div className="font-mono">{item.device_id}</div>
                        {item.model && <div className="text-xs text-slate-500">{item.model}</div>}
                      </TableCell>
                      <TableCell>{item.vehicle_label || <span className="text-slate-400">Sem veículo</span>}</TableCell>
                      <TableCell>
                        <Badge variant={comm.variant}>{comm.label}</Badge>
                        {item.last_seen_at && (
                          <div className="text-xs text-slate-500 mt-0.5" title={formatDateTimeBR(item.last_seen_at)}>
                            {formatAgo(item.last_seen_seconds_ago)}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {item.last_latitude != null && item.last_longitude != null ? (
                          <>
                            <a
                              className="text-emerald-700 hover:underline inline-flex items-center gap-1"
                              href={mapsLink(item.last_latitude, item.last_longitude)}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <MapPin size={14} /> ver no mapa
                            </a>
                            <div className="text-xs text-slate-500">{formatDateTimeBR(item.last_position_at)}</div>
                          </>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {item.last_battery_percent != null ? (
                          <button
                            type="button"
                            title="Ver a curva e a autonomia da bateria"
                            onClick={() => openDetail(item, 'battery')}
                          >
                            <Badge
                              variant={batteryVariant(
                                item.last_battery_percent,
                                item.battery_low_percent ?? DEFAULT_BATTERY_LOW,
                                item.battery_critical_percent ?? DEFAULT_BATTERY_CRITICAL,
                              )}
                            >
                              {item.last_battery_percent}%
                            </Badge>
                          </button>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell className="text-center">{item.last_signal ?? '—'}</TableCell>
                      <TableCell className="text-center">
                        <Badge variant={item.is_active ? 'success' : 'neutral'}>{item.is_active ? 'Ativo' : 'Inativo'}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-2">
                          <Button variant="secondary" size="sm" onClick={() => openDetail(item, 'raw')}>
                            Eventos e mensagens
                          </Button>
                          <Button variant="secondary" size="sm" onClick={() => openForm(item)} icon={<Edit size={16} />} />
                          {deleteConfirm === item.vehicle_tracker_id ? (
                            <div className="flex items-center gap-1">
                              <Button variant="danger" size="sm" onClick={() => handleDelete(item.vehicle_tracker_id)}>
                                Confirmar
                              </Button>
                              <Button variant="secondary" size="sm" onClick={() => setDeleteConfirm(null)}>
                                Cancelar
                              </Button>
                            </div>
                          ) : (
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => setDeleteConfirm(item.vehicle_tracker_id)}
                              icon={<Trash2 size={16} className="text-red-500" />}
                            />
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </tbody>
          </Table>
        </CardContent>
      </Card>

      <TrackerFormDialog
        isOpen={formOpen}
        editing={editing}
        initialDeviceId={initialDeviceId}
        vehicles={vehicles}
        onClose={() => setFormOpen(false)}
        onSaved={() => {
          setFormOpen(false);
          load();
        }}
      />
      <TrackerDetailDialog tracker={detail} initialTab={detailTab} onClose={() => setDetail(null)} />
    </div>
  );
};

export const TrackersView: React.FC = () => (
  <PermissionGuard permission="fleet_tracking.manage" showError>
    <TrackersContent />
  </PermissionGuard>
);
